import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  FeedBackOptionApi,
  FeedBackQuestionAndOptionApi,
  FeedbackHistoryOption,
  FeedbackHistoryQuestionOption,
  StudentwellbeingService,
} from '../studentwellbeing.service';

export interface ShowQuestionPaperDialogData {
  categoryName: string;
  admNo: string;
  feedBackDate: string;
  studentName?: string;
  sectionName?: string;
  questionAndOption: FeedbackHistoryQuestionOption[];
  onDownloadPdf?: (ctx: { admNo: string; feedBackDate: string }) => void;
}

interface StudentSelection {
  optionId: number;
  optionRank: number;
  feedbackOptionText: string;
}

@Component({
  selector: 'app-show-question-paper',
  templateUrl: './show-question-paper.component.html',
  styleUrls: ['./show-question-paper.component.scss'],
})
export class ShowQuestionPaperComponent implements OnInit {
  questions: FeedBackQuestionAndOptionApi[] = [];
  loading = true;
  loadError = '';

  private readonly selectionsByQuestionId = new Map<number, StudentSelection[]>();

  constructor(
    private dialogRef: MatDialogRef<ShowQuestionPaperComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ShowQuestionPaperDialogData,
    private wellbeingService: StudentwellbeingService,
  ) {
    this.buildSelectionsMap();
  }

  ngOnInit(): void {
    this.wellbeingService.getFeedBackQuestionAndOptions(this.data.categoryName).subscribe({
      next: (questions) => {
        this.questions = [...questions].sort((a, b) => a.questionId - b.questionId);
        this.loading = false;
      },
      error: () => {
        this.questions = [];
        this.loadError = 'Unable to load questions. Please try again.';
        this.loading = false;
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  downloadPdf(): void {
    this.data.onDownloadPdf?.({
      admNo: this.data.admNo,
      feedBackDate: this.data.feedBackDate,
    });
  }

  getQuestionText(question: FeedBackQuestionAndOptionApi): string {
    return String(question.questionTitle ?? question.question ?? '').trim();
  }

  getSortedOptions(question: FeedBackQuestionAndOptionApi): FeedBackOptionApi[] {
    return [...(question.options ?? [])].sort((a, b) => a.optionRank - b.optionRank);
  }

  getOptionLabel(questionId: number, opt: FeedBackOptionApi): string {
    const optionName = String(opt.optionName ?? '').trim();
    if (!this.isTextOption({ optionName })) {
      return optionName;
    }
    const selections = this.selectionsByQuestionId.get(questionId) ?? [];
    const match = selections.find(
      (selected) =>
        selected.optionId === Number(opt.optionId) &&
        selected.optionRank === Number(opt.optionRank),
    );
    if (match?.feedbackOptionText) {
      return match.feedbackOptionText;
    }
    const textAnswer = selections.find((selected) => selected.feedbackOptionText)?.feedbackOptionText;
    return textAnswer || optionName;
  }

  isOptionSelected(questionId: number, opt: FeedBackOptionApi): boolean {
    const selections = this.selectionsByQuestionId.get(questionId) ?? [];
    return selections.some(
      (selected) =>
        selected.optionId === Number(opt.optionId) &&
        selected.optionRank === Number(opt.optionRank),
    );
  }

  private buildSelectionsMap(): void {
    (this.data.questionAndOption ?? []).forEach((question) => {
      const selected = (question.options ?? [])
        .filter((opt) => opt.status)
        .map((opt) => ({
          optionId: Number(opt.optionId),
          optionRank: this.normalizeOptionRank(opt.optionRank),
          feedbackOptionText: String(opt.feedbackOptionText ?? '').trim(),
        }));
      if (selected.length) {
        this.selectionsByQuestionId.set(question.questionId, selected);
      }
    });
  }

  private isTextOption(opt: FeedbackHistoryOption | { optionName?: string; option?: string }): boolean {
    const label = String(('optionName' in opt ? opt.optionName : opt.option) ?? '').trim();
    return label.toUpperCase() === 'TEXT';
  }

  private normalizeOptionRank(rank: unknown): number {
    const value = Number(rank);
    return value >= 1 && value <= 5 ? value : 1;
  }
}
